import React, { useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import Badge from '@atlaskit/badge';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useHoverPopoverContentRef } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView$key as OriginalEstimateFragment } from './__generated__/originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView.graphql';
import type { OriginalEstimateReadViewProps } from './types.tsx';
import { useFormatInputValue } from './utils/formatting.tsx';
import messages from './messages.tsx';

/**
 * The OriginalEstimateReadView will show a read only view of OriginalEstimate field.
 * @param props [OriginalEstimateReadViewProps](./types.tsx)
 */
export const OriginalEstimateReadView = ({
	fragmentRef,
	shouldRenderHoverPopover,
	issueFieldId,
}: Omit<OriginalEstimateReadViewProps, 'fragmentRef'> & {
	fragmentRef: OriginalEstimateFragment | null;
}): React.JSX.Element => {
	const data = useFragment<OriginalEstimateFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView on JiraTimeTrackingField {
				originalEstimate {
					timeInSeconds
				}
				timeTrackingSettings {
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
			}
		`,
		fragmentRef,
	);
	const { formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const settingsInput = {
		workingHoursPerDay: data?.timeTrackingSettings?.workingHoursPerDay,
		workingDaysPerWeek: data?.timeTrackingSettings?.workingDaysPerWeek,
		defaultFormat: data?.timeTrackingSettings?.defaultFormat,
		defaultUnit: data?.timeTrackingSettings?.defaultUnit,
	};

	const formatInputValue = useFormatInputValue(settingsInput);
	const timeString = useMemo(
		() => (data ? formatInputValue(data.originalEstimate?.timeInSeconds ?? 0) : null),
		[data, formatInputValue],
	);

	const hoverPopoverContentRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useHoverPopoverContentRef(issueFieldId)
		: null;

	let hoverPopoverContentRef = null;

	if (hoverPopoverContentRefFactory) {
		hoverPopoverContentRef = hoverPopoverContentRefFactory({
			content: timeString,
		});
	}

	if (!data?.originalEstimate?.timeInSeconds && isExperimentEnabled) {
		return <EmptyFieldText emptyText={noValueTextExperiment} />;
	}

	if (timeString === null) {
		return <EmptyFieldText />;
	}

	if (shouldRenderHoverPopover) {
		return (
			// Empty div to allow ellipsis
			// eslint-disable-next-line jira/react/no-empty-divs
			<div>
				<Badge>
					<Text maxLines={1} ref={hoverPopoverContentRef}>
						{timeString}
					</Text>
				</Badge>
			</div>
		);
	}

	return <Badge>{timeString}</Badge>;
};
